/**
 * Shared client-side billing summary helpers.
 *
 * These mirror the server-side "Invoiced" definition implemented in
 * `usp_Invoice_GetByProjectIdPaged` (result set 3) so that demo/mock mode and
 * live mode behave identically.
 *
 * "Invoiced" definition: the gross total of invoices actually raised for a
 * project (so neither Draft nor Void invoices count), net of credit notes. When
 * a credit note is raised the original invoice keeps its
 * gross total but its status becomes Credited (fully credited) or
 * PartiallyCredited (partially credited). Server-side, the credited amount is
 * subtracted using the separate CreditNote table. On the client we do not carry
 * the per-invoice credited amount, so we simply exclude any credit-related row
 * from the invoiced figure. Demo/mock datasets never contain credited invoices,
 * so this stays exact for demo mode; the authoritative netting (including
 * partial credits) is performed server-side.
 */

/** Row shape accepted by the summary helpers — only amount + status are used. */
export interface InvoiceSummaryRow {
  amount: number;
  status: string;
}

/** Row shape accepted by the net summary helper — the net (ex-VAT) amount + status. */
export interface InvoiceNetSummaryRow {
  net: number;
  status: string;
}

/**
 * Invoice statuses that must NOT contribute to the "Invoiced" total:
 *  - Credited / PartiallyCredited: the credit note nets these down (fully or
 *    partially); server-side subtracts the exact credited amount.
 *  - CreditNote: a credit-note row is itself a credit, never invoiced value.
 *  - Draft: not yet raised, so nothing has been invoiced.
 *  - Void: cancelled, so it is no longer owed.
 */
export const CREDIT_EXCLUDED_INVOICE_STATUSES: ReadonlySet<string> = new Set([
  "Credited",
  "PartiallyCredited",
  "CreditNote",
  "Draft",
  "Void",
]);

/**
 * Compute the "Invoiced" total from a list of invoices, excluding draft, void
 * and credited / credit-note rows so that raising a credit note reduces the
 * figure and unraised drafts never inflate it.
 */
export function computeInvoicedTotal(invoices: InvoiceSummaryRow[]): number {
  return invoices
    .filter((inv) => !CREDIT_EXCLUDED_INVOICE_STATUSES.has(inv.status))
    .reduce((sum, inv) => sum + inv.amount, 0);
}

/**
 * Compute the "Invoiced" total excluding VAT, applying the same status
 * exclusions as {@link computeInvoicedTotal}. Used by the billing summary bar so
 * the Invoiced figure and the billing-progress percentage compare like-for-like
 * against the net Budget and Ready to Bill values. Mirrors the server-side
 * TotalInvoicedNet.
 */
export function computeInvoicedNetTotal(invoices: InvoiceNetSummaryRow[]): number {
  return invoices
    .filter((inv) => !CREDIT_EXCLUDED_INVOICE_STATUSES.has(inv.status))
    .reduce((sum, inv) => sum + inv.net, 0);
}

/** Standard VAT rate applied to demo/mock invoice datasets. */
export const DEMO_VAT_RATE = 0.2;

/**
 * Derive the Net / VAT split of a gross demo invoice amount. Live invoices carry
 * subtotal / taxAmount from the API; demo datasets only carry a gross figure.
 */
export function splitGrossIntoNetAndVat(gross: number, rate: number = DEMO_VAT_RATE): { net: number; vat: number } {
  const net = Math.round((gross / (1 + rate)) * 100) / 100;
  return { net, vat: Math.round((gross - net) * 100) / 100 };
}
